import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { RunTimeConfigService } from '@shared/services/run-time-config.service';
// import 'moment/min/locales';
/*
Refer: https://momentjs.com/docs/ (Locale aware formats)

Format    Example                             Description
-----------------------------------------------------------
L	        09/04/1986	                        Date (in local format)
LL	      September 4 1986	                  Month name, day of month, year
LLL	      September 4 1986 8:30 PM	          Month name, day of month, year, time
LLLL	    Thursday, September 4 1986 8:30 PM	Day of week, month name, day of month, year, time
LT	      8:30 PM	                            Time (without seconds)
LTS	      8:30:00 PM	                        Time (with seconds)
-----------------------------------------------------------

Hour, minute, second, millisecond, and offset tokens
-----------------------------------------------------------
Input                     Example           Description
-----------------------------------------------------------
H HH	                    0..23             Hours (24 hour time)
h hh	                    1..12             Hours (12 hour time used with a A.)
k kk	                    1..24             Hours (24 hour time from 1 to 24)
a A	                      am pm	            Post or ante meridiem (Note the one character a p are also considered valid)
m mm	                    0..59	            Minutes
s ss	                    0..59	            Seconds
S SS SSS ... SSSSSSSSS	  0..999999999	    Fractional seconds
Z ZZ	                    +12:00	          Offset from UTC as +-HH:mm, +-HHmm, or Z
*/

@Injectable({
  providedIn: 'root'
})
export class DateTimeService {
  private userLocale: any = 'en';
  private userTimeZone: string;
  private readonly appConfig: any;

  constructor(private runTimeConfigService: RunTimeConfigService) {
    this.appConfig = this.runTimeConfigService.getConfig('appConfig');
  }

  /**
   * @param locale
   * set locale
   */
  setLocale(locale: string) {
    this.userLocale = locale;
    moment().locale(locale);
  }

  setTimeZone(tz: string) {
    console.info('Timezone updated to ' + tz);
    this.userTimeZone = tz;
  }
  getUserTimeZone() {
    return this.userTimeZone;
  }
  /**
   * @param timeMS - time in milliseconds
   * returns moment datetime object by converting ms
   */
  parseTime(timeMS: any) {
    return moment(timeMS);
  }

  /**
   * returns date time as per user locale
   */
  getCurrentDateTime() {
    return moment();
  }

  /**
   * returns time zone as per user locale
   */
  getTimeZone() {
    return moment.tz.guess();
  }

  /**
   * @param zone - for zone
   * returns offset for zone
   */
  getTimeOffset(zone?: string) {
    const _zone = zone ? zone : moment.tz.guess();
    return moment.tz(_zone).format('Z');
  }

  /**
   * @param withOffset - with offset value
   * returns list of time zones
   */
  getTimeZones(withOffset?: boolean, allZones?: boolean) {
    let timeZoneCountries = this.appConfig.timeZoneCountries;
    if (allZones) {
      timeZoneCountries = moment.tz.countries();
    }
    let timeZones = [];
    for (const country of timeZoneCountries) {
      timeZones.push(...moment.tz.zonesForCountry(country, withOffset));
    }

    if (withOffset) {
      // sort by time offset
      timeZones = timeZones.sort((a, b) => (a.offset < b.offset ? -1 : a.offset > b.offset ? 1 : 0)).reverse();
    }
    return timeZones;
  }

  getTimeZonesWithOffsets() {
    let timeZoneCountries = this.appConfig.timeZoneCountries;
    timeZoneCountries = moment.tz.countries();
    const timeZones = [];
    for (const country of timeZoneCountries) {
      timeZones.push(...moment.tz.zonesForCountry(country, true));
    }

    const offsetTmz = [];
    for (const i of timeZones) {
      const timeZoneStr = '(GMT ' + this.getTimeOffset(i.name) + ') ' + i.name;
      offsetTmz.push(timeZoneStr);
    }
    return offsetTmz;
  }

  /**
   * @param dateTime - date and time in UTC
   * @param longDateFormatKey - key to format date/time
   * returns formatted date and time in user locale
   */
  formatToLongDate(dateTime: any, longDateFormatKey?: string) {
    const _dateTime = moment.tz(dateTime, 'UTC').tz(this.userTimeZone);
    const _longDateFormatKey = longDateFormatKey ? longDateFormatKey : 'LLLL'; // if no format was specified use full long format

    return _dateTime.locale(this.userLocale).format(_longDateFormatKey);
  }

  /**
   * @param date - date and time in UTC
   * returns date in user locale. Ex: 09/04/1986
   */
  formatDate(date: string | Date) {
    date = date + ' 00:00:00'; // since no time is specified, we assume the day just started
    const x = moment.tz(date, 'UTC').tz(this.userTimeZone); // moment(_date).tz(this.userTimeZone);
    return x.locale(this.userLocale).format('L');
  }
  formatTime(date: string | Date, formatStr?: string) {
    const _date = moment.tz(date, 'UTC').tz(this.userTimeZone);
    return moment(_date)
      .locale(this.userLocale)
      .format(formatStr ? formatStr : 'L');
  }
  /**
   * @param dateTime - date and time in UTC
   * returns date and time in user locale. Ex: 09/04/1986 8:30 PM
   */
  formatDateTime(dateTime?: string | Date, withSeconds?: boolean, twentyFourHourFormat?: boolean) {
    if (dateTime) {
      const parsedDatetime = moment(new Date(dateTime)).format('yyyy-MM-DD HH:mm:ss');
      const _dateTime = moment.tz(parsedDatetime, 'UTC').tz(this.userTimeZone);
      let formatStr = withSeconds ? 'L LTS' : 'L LT';

      if (twentyFourHourFormat) {
        formatStr = withSeconds ? 'L HH:mm:ss' : 'L HH:mm';
      }

      return moment(_dateTime)
        .locale(this.userLocale)
        .format(formatStr);
    } else {
      return null;
    }
  }

  // formate date for aiops reports
  formatDateAiops(dateTime?: string | Date) {
    console.log('input datetime', dateTime);
    if (dateTime) {
      const parsedDatetime = moment(new Date(dateTime)).format('yyyy-MM-DD');
      const _dateTime = moment.tz(parsedDatetime, 'UTC').tz(this.userTimeZone);
      return moment(_dateTime)
        .locale(this.userLocale)
        .format('L'); // Use 'L' for date format according to the user's locale
    } else {
      return null;
    }
  }

  //

  /**
   * @param dateTime - date and time
   * returns descriptive date and time. ex: October 14, 2020 4:00 PM or if withDay is true - Wednesday, October 14, 2020 4:00 PM
   */
  formatDescriptiveDate(dateTime?: string | Date, withDay?: boolean) {
    const formatStr = withDay ? 'LLLL' : 'LLL';
    return this.formatToLongDate(dateTime, formatStr);
  }

  /**
   * @param dateTime - date and time.
   * returns date and time in UTC
   */
  formatToUTC(dateTime?: string | Date) {
    return moment.utc(moment.tz(dateTime, this.userTimeZone));
  }

  /**
   * @param dateTime - date and time. Picks current if argument is not provided
   * returns formats date and time in UTC and returns ISO representation. ex: 2020-10-16T08:23:37.175Z
   */
  formatToUTCISO(dateTime?: string | Date) {
    return this.formatToUTC(dateTime).toISOString();
  }

  isValidCIAPFormat(date?: string | Date) {
    if (!date) {
      return false;
    }

    return moment(date, 'YYYY-MM-DD', true).isValid();
  }

  /**
   * @param dateTime - date and time. Picks current if argument is not provided
   * @param withTime - true or false if time is required in return value
   * @param twentyFourHourFormat - true or false if return value for time must be in 24 hour format
   * @param dontConvertToUTC - if for any corner scenarios UTC conversion is not required
   * returns converts to UTC and formats date and time in CIAP format
   */
  formatDateTimeToCIAPFormat(
    dateTime?: string | Date,
    withTime?: boolean,
    twentyFourHourFormat?: boolean,
    dontConvertToUTC?: boolean,
    withTimeOnly?: boolean,
    returnCurrentTime?: boolean
  ) {
    const currentZone = this.getUserTimeZone();
    let formatStr = 'YYYY-MM-DD';
    if (withTime && !twentyFourHourFormat) {
      formatStr += ' hh:mm:ss A';
    } else if (withTime && twentyFourHourFormat) {
      formatStr += ' HH:mm:ss';
    }

    if (typeof dateTime === 'string' && !withTime) {
      dateTime =
        dateTime +
        ' ' +
        moment()
          .format('HH:mm:ss')
          .toString();
    } else {
      dateTime =
        moment(dateTime)
          .format('YYYY-MM-DD')
          .toString() +
        ' ' +
        moment(dateTime)
          .format('HH:mm:ss')
          .toString();
    }
    if (withTimeOnly) {
      formatStr = 'HH:mm:ss';
    }
    if (!withTime && returnCurrentTime) {
      formatStr += ' HH:mm:ss';
    }

    if (dontConvertToUTC) {
      return moment.tz(dateTime, this.userTimeZone).format(formatStr);
    } else {
      const d = new Date().getTimezoneOffset();
      if (d !== 0) {
        return this.formatToUTC(dateTime).format(formatStr);
      } else {
        return moment(dateTime).format(formatStr);
      }
      // this.formatToUTC(dateTime).format(formatStr);
    }
  }
  getDiffInDays(fromdate: any, todate: any) {
    if (moment(fromdate).isAfter(moment(todate))) {
      return moment(fromdate).diff(moment(todate), 'days') + 1;
    } else {
      return Math.abs(moment(fromdate).diff(moment(todate), 'days') - 1);
    }
  }
  getDurationRange(durationValue: any, forFuture: boolean) {
    const fromDate = moment().format('YYYY-MM-DD');
    let toDate;
    if (forFuture) {
      toDate = moment()
        .add(+durationValue - 1, 'days')
        .format('YYYY-MM-DD');
    } else {
      toDate = moment()
        .subtract(+durationValue - 1, 'days')
        .format('YYYY-MM-DD');
    }

    return {
      fromDate,
      toDate
    };
  }
  getTime(date?: any, twelveHourFormat?: boolean) {
    let formatStr = 'HH:mm:ss';
    if (twelveHourFormat) {
      formatStr += ' hh:mm:ss A';
    }
    if (date) {
      return moment(date).format(formatStr);
    } else {
      return moment().format(formatStr);
    }
  }
  addDays(date: any, days: number) {
    // console.log('moment', moment(date));
    return moment(date)
      .add(days, 'days')
      .format('YYYY-MM-DD HH:mm:ss');
  }
  getcurrentDateValue() {
    const currentZone = this.getUserTimeZone();
    const timeZones = this.getTimeZones(true, true);
    let valDiff;
    if (currentZone === 'utc') {
      valDiff = 0;
    } else {
      for (const i in timeZones) {
        if (currentZone === timeZones[i].name) {
          valDiff = timeZones[i].offset / 60;
        }
      }
    }
    const e = new Date();

    const utcCtime = this.subtractTimeFromDate(e, 5.5);
    const m = this.subtractTimeFromDate(utcCtime, valDiff);
    return m;
  }

  subtractTimeFromDate(objDate: any, intHours: any) {
    const numberOfMlSeconds = objDate.getTime();
    const addMlSeconds = intHours * 60 * 60 * 1000;
    const newDateObj = new Date(numberOfMlSeconds - addMlSeconds);
    return newDateObj;
  }
}
